import { OccupancyBlockCostClass } from '../models/occupancy-dashboard.model';

/**
 * View shape of the occupancy dashboard: the dataset laid out night by night.
 *
 * Every unit carries one slot per night of the window, indexed from the window's first day, so the
 * dashboard can re-filter and re-aggregate on every click without re-reading dates.
 */

/** What a unit-night was. A night counts once, in one state only. */
export enum NightStatus {
  /** Listed, not sold, not blocked: inventory that went unsold. */
  FREE = 0,
  SOLD = 1,
  BLOCKED = 2,
  /** Not listed that day and not sold: never inventory, so outside every denominator. */
  UNLISTED = 3
}

export interface DashboardMonth {
  y: number;
  /** 0-11 */
  m: number;
  /** Axis label: 'Oct', or 'Jan 26' at a year boundary. */
  label: string;
  long: string;
}

export interface DashboardCalendar {
  /** First day of the window, 'YYYY-MM-DD'. */
  start: string;
  totalDays: number;
  /** Night index of the report date. Nights before it are closed. */
  today: number;
  /** Night index of 31 December of the report date's year. */
  yearEnd: number;
  months: DashboardMonth[];
  /** Month index of each night. */
  monthOfDay: Int16Array;
  monthFirstDay: number[];
  monthLength: number[];
}

export interface DashboardBooking {
  reservationId: string;
  /** Night index of check-in; may be negative for a stay that began before the window. */
  start: number;
  nights: number;
  /** Night index the reservation was created on. */
  bookedAt: number;
  /** Night index it was canceled on, or null for a live stay. */
  canceledAt: number | null;
  source: string;
  nationality: string;
}

export interface DashboardBlock {
  id: string;
  start: number;
  nights: number;
  /** Block type, from the block's flags: Owner stay, Maintenance, or Other. */
  reason: string;
  description: string;
  costClass: OccupancyBlockCostClass;
  /** Who raised it. */
  owner: string;
  createdAt: number | null;
}

export interface DashboardUnit {
  /** The unit code shown as "Unit ID", e.g. BN-01. */
  id: string;
  roomId: string;
  name: string;
  project: string;
  area: string;
  building: string;
  landlord: string;
  unitType: string;
  unitTypeGroup: string;
  view: string;
  currency: string;
  status: Uint8Array;
  /** Index into DashboardPortfolio.sources for sold nights, -1 otherwise. */
  srcOfDay: Int8Array;
  /** Index into DashboardPortfolio.nationalities for sold nights, -1 otherwise. */
  natOfDay: Int16Array;
  /** EGP earned on each sold night; 0 when the stay has not been billed yet. */
  rateOfDay: Float32Array;
  /** Index into `blocks` of the block that owns each blocked night, -1 otherwise. */
  blockOfDay: Int16Array;
  bookings: DashboardBooking[];
  blocks: DashboardBlock[];
}

export interface DashboardPortfolio {
  calendar: DashboardCalendar;
  units: DashboardUnit[];
  sources: string[];
  nationalities: string[];
  reasons: string[];
}
